import React, { useState, useEffect, useContext } from 'react';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Layout from '../components/Layout';
import { Clock, Calendar, Play, Square, Coffee } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const EmployeeDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activeEntry, setActiveEntry] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let interval;
    if (activeEntry) {
      interval = setInterval(() => {
        const start = new Date(activeEntry.start_time);
        const now = new Date();
        const diff = Math.floor((now - start) / 1000);
        setElapsedTime(diff);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [activeEntry]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
      setActiveEntry(response.data.active_entry);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="employee-dashboard">
        <div>
          <h1 className="text-4xl font-bold text-gray-900">Willkommen zurück!</h1>
          <p className="text-gray-600 mt-2 text-lg">{user?.first_name} {user?.last_name}</p>
        </div>

        {/* Active Timer */}
        {activeEntry && (
          <Card className="border-l-4 border-l-green-500 bg-gradient-to-br from-green-50 to-emerald-50" data-testid="active-timer-card">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="bg-green-500 p-3 rounded-full animate-pulse">
                    <Clock className="w-8 h-8 text-white" />
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-2">Aktive Zeiterfassung</p>
                <div className="text-6xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk' }} data-testid="active-timer-display">
                  {formatTime(elapsedTime)}
                </div>
                <p className="text-sm text-gray-600">Gestartet um {format(new Date(activeEntry.start_time), 'HH:mm', { locale: de })} Uhr</p>
                <div className="mt-6">
                  <Button
                    onClick={() => navigate('/tracking')}
                    size="lg"
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="view-active-entry-button"
                  >
                    <Square className="w-4 h-4 mr-2" />
                    Ausstempeln
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="card-hover border-l-4 border-l-blue-500" data-testid="hours-today-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Heute</CardTitle>
              <Clock className="w-5 h-5 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{stats?.total_hours_today?.toFixed(2) || 0}h</div>
            </CardContent>
          </Card>

          <Card className="card-hover border-l-4 border-l-purple-500" data-testid="hours-week-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Diese Woche</CardTitle>
              <Calendar className="w-5 h-5 text-purple-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{stats?.total_hours_week?.toFixed(2) || 0}h</div>
            </CardContent>
          </Card>

          <Card className="card-hover border-l-4 border-l-indigo-500" data-testid="hours-month-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-600">Dieser Monat</CardTitle>
              <Calendar className="w-5 h-5 text-indigo-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-gray-900">{stats?.total_hours_month?.toFixed(2) || 0}h</div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="card-hover">
          <CardHeader>
            <CardTitle>Schnellzugriff</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button
                onClick={() => navigate('/tracking')}
                className="h-20 text-base bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                data-testid="start-tracking-button"
              >
                <Play className="w-5 h-5 mr-2" />
                {activeEntry ? 'Zeiterfassung anzeigen' : 'Zeiterfassung starten'}
              </Button>
              <Button
                onClick={() => navigate('/calendar')}
                className="h-20 text-base"
                variant="outline"
                data-testid="view-calendar-button"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Kalender anzeigen
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recent Entries */}
        <Card className="card-hover">
          <CardHeader>
            <CardTitle>Letzte Einträge</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_entries && stats.recent_entries.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_entries.map((entry) => (
                  <div key={entry.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-testid={`recent-entry-${entry.id}`}>
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${
                        entry.status === 'active' ? 'bg-green-100' : 'bg-gray-100'
                      }`}>
                        {entry.status === 'active' ? (
                          <Clock className="w-5 h-5 text-green-600" />
                        ) : (
                          <Coffee className="w-5 h-5 text-gray-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{entry.date}</p>
                        <p className="text-sm text-gray-600">
                          {format(new Date(entry.start_time), 'HH:mm', { locale: de })} - 
                          {entry.end_time ? format(new Date(entry.end_time), 'HH:mm', { locale: de }) : 'Aktiv'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {entry.status === 'completed' && (
                        <p className="text-lg font-bold text-gray-900">{entry.total_hours?.toFixed(2)}h</p>
                      )}
                      {entry.status === 'active' && (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          Aktiv
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">Keine Einträge vorhanden</p>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default EmployeeDashboard;
